import { useEffect, useMemo, useState } from "react"

export type SortItem = {
  id: string
  desc: boolean
}

export type TablePrefs = {
  columnOrder: string[]
  columnWidths: Record<string, number>
  columnVisibility: Record<string, boolean>
  sorting: SortItem[]
}

export type ColumnMeta = {
  id: string
  label: string
  defaultWidth: number
  defaultVisible: boolean
  pinned?: boolean // pinned columns are always first and cannot be hidden
  icon?: any
}

function storageKey(tableKey: string) {
  return `tablePrefs:${tableKey}`
}

function createDefaultPrefs(columns: ColumnMeta[]): TablePrefs {
  const pinnedIds = columns.filter((c) => c.pinned).map((c) => c.id)
  const restIds = columns.filter((c) => !c.pinned && c.defaultVisible).map((c) => c.id)
  const allIds = columns.map((c) => c.id)

  return {
    columnOrder: [...pinnedIds, ...restIds, ...allIds.filter((id) => !pinnedIds.includes(id) && !restIds.includes(id))],
    columnWidths: Object.fromEntries(columns.map((c) => [c.id, c.defaultWidth])),
    columnVisibility: Object.fromEntries(columns.map((c) => [c.id, c.pinned ? true : c.defaultVisible])),
    sorting: [],
  }
}

function reconcilePrefs(saved: Partial<TablePrefs>, columns: ColumnMeta[]): TablePrefs {
  const defaults = createDefaultPrefs(columns)
  const ids = new Set(columns.map((c) => c.id))
  const pinnedIds = new Set(columns.filter((c) => c.pinned).map((c) => c.id))

  // Column order: keep saved order for known columns, append new columns at the end
  const savedOrder = (saved.columnOrder ?? []).filter((id) => ids.has(id))
  const missingIds = columns.filter((c) => !savedOrder.includes(c.id)).map((c) => c.id)
  const rawOrder = [...savedOrder, ...missingIds]
  // Ensure pinned always first
  const pinnedFirst = [...columns.filter((c) => c.pinned).map((c) => c.id), ...rawOrder.filter((id) => !pinnedIds.has(id))]

  return {
    columnOrder: pinnedFirst,
    columnWidths: { ...defaults.columnWidths, ...Object.fromEntries(Object.entries(saved.columnWidths ?? {}).filter(([id]) => ids.has(id))) },
    columnVisibility: {
      ...defaults.columnVisibility,
      ...Object.fromEntries(Object.entries(saved.columnVisibility ?? {}).filter(([id]) => ids.has(id))),
      // pinned columns are always visible
      ...Object.fromEntries(columns.filter((c) => c.pinned).map((c) => [c.id, true])),
    },
    sorting: (saved.sorting ?? []).filter((item) => ids.has(item.id)).slice(0, 1),
  }
}

function readPrefs(tableKey: string, columns: ColumnMeta[]): TablePrefs {
  const fallback = createDefaultPrefs(columns)
  if (typeof window === "undefined") return fallback
  const raw = window.localStorage.getItem(storageKey(tableKey))
  if (!raw) return fallback
  try {
    return reconcilePrefs(JSON.parse(raw) as Partial<TablePrefs>, columns)
  } catch {
    return fallback
  }
}

export function useTablePrefs(tableKey: string, columns: ColumnMeta[]) {
  const columnSignature = useMemo(
    () => columns.map((c) => `${c.id}:${c.defaultWidth}:${c.defaultVisible}`).join("|"),
    [columns],
  )

  const [prefs, setPrefs] = useState<TablePrefs>(() => readPrefs(tableKey, columns))

  // Re-reconcile when columns change (e.g. new attributes loaded)
  useEffect(() => {
    setPrefs((current) => reconcilePrefs(current, columns))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSignature])

  // Persist to localStorage
  useEffect(() => {
    window.localStorage.setItem(storageKey(tableKey), JSON.stringify(prefs))
  }, [tableKey, prefs])

  return [prefs, setPrefs] as const
}
